import * as React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';

import api from '../../services/api';
import { Paper, Button, Typography, Container } from '@mui/material';
import Grid from "@mui/material/Grid2";
import DeleteIcon from '@mui/icons-material/Delete';
import { useStoreActions } from 'easy-peasy';
import DialogoCNAE from './DialogoCNAE';
import { NoRowsOverlay } from '../../components';
import DialogoAtividade from './DialogoAtividade';

const AdminCNAEs = () => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);

  const [rows, setRows] = React.useState([]);
  const [atividades, setAtividades] = React.useState([]);
  const [openDlgCNAE, setOpenDlgCNAE] = React.useState(false);
  const [openDlgAtividade, setOpenDlgAtividade] = React.useState(false);
  const [cnae, setCnae] = React.useState(null);
  const [atv, setAtv] = React.useState(null);

  const deleteCNAE = React.useCallback(
    id => async () => {
      startLoading();
      try {
        await api.http.delete('/cnaes/' + id);
        stopLoading({ severity: 'success' });
        reloadCNAEs();
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: 'error'
        });
      }
    },
    []
  );

  const deleteActivity = React.useCallback(
    id => async () => {
      startLoading();
      try {
        await api.http.delete('/atividades/' + id);
        stopLoading({ severity: 'success' });
        reloadActivity();
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: 'error'
        });
      }
    },
    []
  );

  const columns = React.useMemo(
    () => [
      {
        field: 'codigo',
        headerName: 'Código',
        flex: 1
      },
      {
        field: 'atividade',
        headerName: 'Atividade Principal',
        flex: 1,
        valueGetter: (value, row) => {
          const _atividades = atividades.find(
            act => act.id === row.atividadePrincipalId
          );
          return _atividades ? _atividades.descr : '';
        }
      },
      {
        field: 'descr',
        headerName: 'Descrição',
        flex: 3
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Remover',
        width: 100,
        getActions: params => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={deleteCNAE(params.id)}
          />
        ]
      }
    ],
    [deleteCNAE, atividades]
  );
  const columnsAtividade = React.useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1
      },
      {
        field: 'descr',
        headerName: 'Descrição',
        flex: 3
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Remover',
        width: 100,
        getActions: params => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={deleteActivity(params.id)}
          />
        ]
      }
    ],
    [deleteActivity]
  );

  const adiciona = async () => {
    setOpenDlgCNAE(true);
  };
  const adicionaAtividade = async () => {
    setOpenDlgAtividade(true);
  };

  const onRowClick = (params, event, details) => {
    setCnae(params.row);
    setOpenDlgCNAE(true);
  };
  const onRowAtvClick = (params, event, details) => {
    setAtv(params.row);
    setOpenDlgAtividade(true);
  };

  const onCloseDlgCNAE = (reload = false) => {
    setCnae(null);
    setOpenDlgCNAE(false);
    if (reload) {
      reloadCNAEs();
    }
  };
  const onCloseDlgAtividade = (reload = false) => {
    setAtv(null);
    setOpenDlgAtividade(false);
    if (reload) {
      reloadActivity();
    }
  };
  const reloadActivity = async mount => {
    startLoading();
    try {
      let atividades = (await api.http.get('/atividades')).data;
      if (!mount || mount.isMounted) {
        setAtividades(atividades);
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };
  const reloadCNAEs = async mount => {
    startLoading();
    try {
      let cnaes = (await api.http.get('/cnaes')).data;
      if (!mount || mount.isMounted) {
        setRows(cnaes);
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    reloadCNAEs(mount);
    reloadActivity(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  return (
    (<Container maxWidth="false">
      <DialogoCNAE
        atividades={atividades}
        open={openDlgCNAE}
        onClose={onCloseDlgCNAE}
        cnae={cnae}
      />
      <Grid container spacing={3} sx={{ my: 2 }}>
        <Grid style={{ textAlign: 'left' }} size="grow">
          <Typography variant="h4">CNAEs</Typography>
        </Grid>
        <Grid style={{ textAlign: 'right' }} size="grow">
          <Button color="primary" variant="outlined" onClick={adiciona}>
            Adicionar
          </Button>
        </Grid>
      </Grid>
      <Paper
        variant="elevation"
        elevation={2}
        style={{
          height: 400,
          width: '100%',
          marginBottom: 60
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          onRowClick={onRowClick}
          slots={{ noRowsOverlay: NoRowsOverlay }}
          noRowsText="Sem registros"
          disableSelectionOnClick
        />
      </Paper>
      <DialogoAtividade
        open={openDlgAtividade}
        onClose={onCloseDlgAtividade}
        atividade={atv}
      />
      <Grid container spacing={3} sx={{ my: 2 }}>
        <Grid style={{ textAlign: 'left' }} size="grow">
          <Typography variant="h4">Atividades Principais</Typography>
        </Grid>
        <Grid style={{ textAlign: 'right' }} size="grow">
          <Button
            color="primary"
            variant="outlined"
            onClick={adicionaAtividade}
          >
            Adicionar
          </Button>
        </Grid>
      </Grid>
      <Paper
        variant="elevation"
        elevation={2}
        style={{
          height: 400,
          width: '100%',
          marginBottom: 60
        }}
      >
        <DataGrid
          rows={atividades}
          columns={columnsAtividade}
          pageSize={5}
          rowsPerPageOptions={[5]}
          onRowClick={onRowAtvClick}
          slots={{ noRowsOverlay: NoRowsOverlay }}
          noRowsText="Sem registros"
          disableSelectionOnClick
        />
      </Paper>
    </Container>)
  );
};

export default AdminCNAEs;
